import get from 'lodash-es/get';
import pick from 'lodash-es/pick';
import type { DeepReadonly } from 'ts-essentials';

import type {
  AnalyticsDeliveryAddress,
  AnalyticsEvent,
} from '@jane/shared-ecomm/types';
/* eslint-disable-next-line no-restricted-imports */
import type { Cart, PendingCartProduct } from '@jane/shared/models';
import type { CartLineItems } from '@jane/shared/types';
import { postMessageToIframeParent } from '@jane/shared/util';

export const postAnalyticsEvent = (payload: AnalyticsEvent) => {
  postMessageToIframeParent({
    messageType: 'analyticsEvent',
    payload,
  });
};

export const cartProductList = (
  cartProducts: DeepReadonly<PendingCartProduct[]>
) =>
  cartProducts.map((product) => {
    const analyticsProperties = pick(product, [
      'product_id',
      'name',
      'brand',
      'category',
      'kind',
      'count',
      'special_id',
      'special_title',
      'price_id',
    ]);
    const unit_price = Number(
      get(product, `discounted_price_${product.price_id}`) ||
        get(product, `price_${product.price_id}`)
    );

    return {
      ...analyticsProperties,
      unit_price,
    };
  });

const cartBrandNamesAndIds = (
  cartProducts: DeepReadonly<PendingCartProduct[]>
) => {
  const brandNames = cartProducts
    .filter((p) => !!p.brand)
    .map((p) => p.brand!.trim())
    .sort();

  const brandIds = cartProducts
    .filter((p) => !!p.product_brand_id)
    .map((p) => p.product_brand_id)
    .sort();

  return {
    names: Array.from(new Set(brandNames)).join(','),
    ids: Array.from(new Set(brandIds)).join(','),
  };
};

interface PostCheckoutEventArgs {
  cart: Cart;
  cartLineItems: Partial<CartLineItems> & { total: CartLineItems['total'] };
  deliveryAddress?: AnalyticsDeliveryAddress | null;
  isStackAdaptPixelEnabled: boolean;
  orderCustomer: {
    birthDate?: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
  };
}

export const postCheckoutAnalyticsEvent = ({
  cart,
  cartLineItems,
  deliveryAddress,
  isStackAdaptPixelEnabled,
  orderCustomer,
}: PostCheckoutEventArgs) => {
  const cartProducts = cartProductList(cart.products);
  const cartBrands = cartBrandNamesAndIds(cart.products);

  const properties = {
    /** @todo Send store currency for analytics events */
    currency: null,
    estimatedTotal: cartLineItems.total,
    deliveryFee: cartLineItems.deliveryFee,
    discountTotal: cartLineItems.discountTotal,
    preDiscountSubtotal: cartLineItems.preDiscountSubtotal,
    salesTax: cartLineItems.salesTax,
    serviceFee: cartLineItems.serviceFee,
    storeId: cart.store?.id,
    storeTax: cartLineItems.storeTax,
    cartId: cart.id!,
    paymentMethod: cart.payment_method,
    products: cartProducts,
    brandNames: cartBrands.names,
    brandIds: cartBrands.ids,
    reservationMode: cart.reservation_mode,
    customerFirstName: orderCustomer.firstName,
    customerLastName: orderCustomer.lastName,
    customerEmail: orderCustomer.email,
    customerPhone: orderCustomer.phone,
    customerBirthDate: orderCustomer.birthDate,
    deliveryWindowStartTime: cart.delivery_started_at,
    deliveryWindowEndTime: cart.delivery_finished_at,
    storeNotes: cart.message,
    deliveryAddress,
    uuid: cart.uuid,
    tags: cart.tags,
    isStackAdaptPixelEnabled,
  };
  postAnalyticsEvent({
    name: 'checkout',
    properties,
  });
};
